import React from 'react';
import {
  LayoutGrid, Eye, Database, Activity,
  ChevronLeft, ChevronRight, Wifi, Server, Sparkles,
} from 'lucide-react';
import EyeLogo from './EyeLogo';
import { useApp } from '../contexts/AppContext';
import { HORUS_VERSION } from '../lib/version';

// Inline SVG fallback — render saat /kes-icon-*.svg gagal load
// (misal Canva SVG dengan c2pa metadata + raster yang berat di small size)
const KesIconInline = ({ theme, size = 14 }) => {
  const outer  = theme === 'dark' ? '#3b82f6' : '#1e3a8a';
  const inner  = theme === 'dark' ? '#60a5fa' : '#3b82f6';
  const center = theme === 'dark' ? '#93c5fd' : '#60a5fa';
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <line x1="10" y1="10" x2="10" y2="2"    stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="10" y2="18"   stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="2"  y2="10"   stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="18" y2="10"   stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="3.5"  y2="3.5"  stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="16.5" y2="3.5"  stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="3.5"  y2="16.5" stroke={outer} strokeWidth="1.4"/>
      <line x1="10" y1="10" x2="16.5" y2="16.5" stroke={outer} strokeWidth="1.4"/>
      <circle cx="10"   cy="2"    r="1.7" fill={outer}/>
      <circle cx="10"   cy="18"   r="1.7" fill={outer}/>
      <circle cx="2"    cy="10"   r="1.7" fill={outer}/>
      <circle cx="18"   cy="10"   r="1.7" fill={outer}/>
      <circle cx="3.5"  cy="3.5"  r="1.7" fill={outer}/>
      <circle cx="16.5" cy="3.5"  r="1.7" fill={outer}/>
      <circle cx="3.5"  cy="16.5" r="1.7" fill={outer}/>
      <circle cx="16.5" cy="16.5" r="1.7" fill={outer}/>
      <circle cx="6.2"  cy="6.2"  r="1.8" fill={inner}/>
      <circle cx="13.8" cy="6.2"  r="1.8" fill={inner}/>
      <circle cx="6.2"  cy="13.8" r="1.8" fill={inner}/>
      <circle cx="13.8" cy="13.8" r="1.8" fill={inner}/>
      <circle cx="10"   cy="10"   r="2.4" fill={center}/>
    </svg>
  );
};

const Sidebar = ({ page, setPage, open, setOpen }) => {
  const { t, theme, lang } = useApp();
  const isDark = theme === 'dark';

  const navItems = [
    { id: 'neo-command',    label: 'N.E.O Command Center', sub: lang === 'id' ? 'Pusat Komunikasi N.E.O' : 'N.E.O Communication Hub', icon: Sparkles, badge: lang === 'id' ? 'SEGERA' : 'SOON' },
    { id: 'command-center', label: 'Dashboard',            sub: lang === 'id' ? 'Ikhtisar dan CCTV Aktif' : 'Overview & Active CCTV', icon: LayoutGrid },
    { id: 'neovision',      label: 'Vision',               sub: lang === 'id' ? 'Deteksi Orang dan Kendaraan' : 'People & Vehicle Detection', icon: Eye },
    { id: 'neointel',       label: 'Profiling',            sub: lang === 'id' ? 'Penggalian Data Mendalam' : 'Deep Data Profiling', icon: Database },
    { id: 'datacenter',     label: 'Datacenter',           sub: lang === 'id' ? 'Kelola Orang dan Kendaraan' : 'Manage People & Vehicles', icon: Server },
    { id: 'system-log',     label: lang === 'id' ? 'Monitor Sistem' : 'System Monitor', sub: lang === 'id' ? 'Log & Status Realtime' : 'Logs & Realtime Status', icon: Activity },
  ];

  return (
    <aside
      className="flex flex-col shrink-0 transition-all duration-300 relative z-40"
      style={{ width: open ? 248 : 64, background: 'var(--color-surface)', borderRight: '1px solid var(--border-dim)' }}
    >
      {/* Header — HORUS wordmark (besar) + V2.x badge + "Powered by KES" line. */}
      <div className={`shrink-0 ${open ? 'px-4' : 'px-2'} pt-3 pb-3 relative`} style={{ borderBottom: '1px solid var(--border-lo)' }}>
        <div className={open ? 'flex items-center gap-2' : 'flex flex-col items-center gap-2'}>
          {/* HORUS wordmark — img src DIRECT untuk preserve warna asli SVG.
              JANGAN pakai CSS mask atau backgroundColor override (memory rule).
              Per theme: dark/light SVG file dengan warna asli design Andra. */}
          {open ? (
            // Dual-mount cross-fade — kedua variant ter-load, opacity toggle.
            <div
              role="img"
              aria-label={`HORUS ${HORUS_VERSION}`}
              className="relative shrink-0"
              style={{ height: 64, width: 170 }}
            >
              <img
                src="/horus-wordmark-dark.svg"
                alt={`HORUS ${HORUS_VERSION}`}
                aria-hidden={!isDark}
                className="absolute inset-0"
                style={{
                  height: 64, width: 170, objectFit: 'contain', objectPosition: 'left center',
                  opacity: isDark ? 1 : 0,
                  transition: 'opacity 200ms ease',
                }}
              />
              <img
                src="/horus-wordmark-light.svg"
                alt=""
                aria-hidden={isDark}
                className="absolute inset-0"
                style={{
                  height: 64, width: 170, objectFit: 'contain', objectPosition: 'left center',
                  opacity: isDark ? 0 : 1,
                  transition: 'opacity 200ms ease',
                }}
              />
            </div>
          ) : (
            // Dual-mount cross-fade — kedua variant ter-load + ter-cache, swap
            // smooth lewat opacity (transition 200ms). Tanpa src swap = no flash
            // saat user toggle theme.
            <div className="relative shrink-0" style={{ height: 44, width: 44, minWidth: 44 }}>
              <img
                src="/horus-header-icon-sidebar%20darkmode.svg"
                alt="HORUS"
                aria-hidden={!isDark}
                className="absolute inset-0"
                style={{
                  height: 44, width: 44, objectFit: 'contain', objectPosition: 'center',
                  opacity: isDark ? 1 : 0,
                  transition: 'opacity 200ms ease',
                }}
              />
              <img
                src="/horus-header-icon-sidebar%20lightmode.svg"
                alt=""
                aria-hidden={isDark}
                className="absolute inset-0"
                style={{
                  height: 44, width: 44, objectFit: 'contain', objectPosition: 'center',
                  opacity: isDark ? 0 : 1,
                  transition: 'opacity 200ms ease',
                }}
              />
            </div>
          )}
          {open && (
            <span className="text-[11px] font-black font-mono px-2 py-1 rounded shrink-0"
              style={{
                border: '1px solid rgba(0,119,182,0.5)',
                background: 'rgba(0,119,182,0.10)',
                color: '#0077b6',
                lineHeight: 1,
                letterSpacing: '0.06em',
              }}>
              {HORUS_VERSION}
            </span>
          )}
          <button
            onClick={() => setOpen(!open)}
            className={`${open ? 'ml-auto' : ''} shrink-0 flex items-center justify-center rounded-lg transition-all hover:bg-white/5`}
            style={{
              width: 28,
              height: 28,
              border: open ? 'none' : '1px solid var(--border-dim)',
            }}
            title={open
              ? (lang === 'id' ? 'Tutup sidebar' : 'Collapse sidebar')
              : (lang === 'id' ? 'Buka sidebar' : 'Expand sidebar')
            }
          >
            {open
              ? <ChevronLeft size={14} color="var(--text-dim)" />
              : <ChevronRight size={14} color="var(--text-dim)" />
            }
          </button>
        </div>

        {/* C-001: "Powered by [KES icon] KES" — absolute bottom-right dari logo block.
            Sprint 003 rule: KES SVG icon HARUS mendahului teks KES. */}
        {open && (
          <div className="absolute bottom-2 right-4 flex items-center gap-1.5">
            <span className="text-[9px] font-mono font-bold"
              style={{ color: 'var(--text-dim)', letterSpacing: '0.03em', lineHeight: 1 }}>
              Powered by
            </span>
            {/* Dual-mount cross-fade KES icon — kedua variant render bersamaan,
                opacity toggle by theme. Tidak ada src swap = no flash saat
                theme switch. */}
            <span className="relative inline-block" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <img
                src="/kes-icon-dark.svg"
                alt="KES"
                aria-hidden={!isDark}
                width={14}
                height={14}
                style={{
                  position: 'absolute', inset: 0, objectFit: 'contain',
                  opacity: isDark ? 1 : 0,
                  transition: 'opacity 200ms ease',
                }}
              />
              <img
                src="/kes-icon-light.svg"
                alt=""
                aria-hidden={isDark}
                width={14}
                height={14}
                style={{
                  position: 'absolute', inset: 0, objectFit: 'contain',
                  opacity: isDark ? 0 : 1,
                  transition: 'opacity 200ms ease',
                }}
              />
            </span>
            <span className="text-[9px] font-black font-mono"
              style={{ color: 'var(--color-cyan)', letterSpacing: '0.1em', lineHeight: 1 }}>
              KES
            </span>
          </div>
        )}
      </div>

      {/* Navigasi */}
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto c-scroll">
        {navItems.map(({ id, label, sub, icon: Icon, badge }) => {
          const active = page === id;
          return (
            <button
              key={id}
              onClick={() => setPage(id)}
              title={!open ? label : undefined}
              className="w-full flex items-center gap-2.5 rounded-xl transition-all duration-200 group relative"
              style={{
                padding: open ? '8px 10px' : '9px',
                justifyContent: open ? 'flex-start' : 'center',
                background: active ? 'rgba(0,212,255,0.10)' : 'transparent',
                border: active ? '1px solid rgba(0,212,255,0.22)' : '1px solid transparent',
              }}
            >
              {active && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full"
                  style={{ width: 3, height: 18, background: 'var(--color-cyan)' }} />
              )}
              <div className="relative shrink-0">
                <Icon size={17} color={active ? 'var(--color-cyan)' : 'var(--text-dim)'} />
              </div>
              {open && (
                <div className="text-left min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[11px] font-bold truncate"
                      style={{ color: active ? 'var(--color-cyan)' : 'var(--text-mid)', fontFamily: 'IBM Plex Mono' }}>
                      {label}
                    </p>
                    {badge && (
                      <span className="text-[7px] font-black font-mono px-1.5 py-0.5 rounded shrink-0"
                        style={{
                          background: 'rgba(255,214,10,0.12)',
                          border: '1px solid rgba(255,214,10,0.35)',
                          color: '#ffd60a',
                          letterSpacing: '0.08em',
                          lineHeight: 1,
                        }}>
                        {badge}
                      </span>
                    )}
                  </div>
                  <p className="text-[8.5px] truncate mt-0.5" style={{ color: 'var(--text-mute)' }}>{sub}</p>
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer indikator CCTV */}
      <div className="px-3 py-3 shrink-0" style={{ borderTop: '1px solid var(--border-lo)' }}>
        {open ? (
          <div className="flex items-center gap-2">
            <Wifi size={12} color="var(--color-cyan)" />
            <span className="text-[9px] font-bold" style={{ color: 'var(--text-dim)' }}>{t('cctv_online')}</span>
          </div>
        ) : (
          <div className="flex justify-center">
            <Wifi size={14} color="var(--color-cyan)" />
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
